import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../context/AuthContext";
import {
  Sparkles, Mic, Image as ImageIcon, Youtube, Bell, TrendingUp, Check, ChevronDown,
  ArrowRight, ShieldCheck, Clock, Heart, Scissors, Repeat, BatteryLow, CalendarDays,
  UploadCloud, CalendarX, Lock, Menu, X,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Seo, { SITE_URL } from "../components/Seo";
import SeatBadge, { fmtWeekEnd as fmtWeekEndShared } from "../components/SeatBadge";
import api from "../lib/api";

const BENEFITS_IMG = "https://images.pexels.com/photos/6611925/pexels-photo-6611925.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000";

const btnPrimary = "inline-flex items-center justify-center gap-2 bg-brand-600 text-white hover:bg-brand-700 hover:scale-[1.02] active:scale-[0.98] transition-all text-base md:text-lg font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl";
const btnSecondary = "inline-flex items-center justify-center gap-2 bg-white text-brand-700 border-2 border-brand-600 hover:bg-brand-50 transition-all text-base md:text-lg font-bold px-8 py-4 rounded-full";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`px-6 md:px-12 py-20 md:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className={className}>
    {children}
  </motion.div>
);


const FEATURE_ICONS = [Repeat, TrendingUp, CalendarDays, Bell, Sparkles, Mic, ImageIcon, UploadCloud];
// Rangos [inicio, fin) sobre FEATURE_ICONS/features que forman cada grupo, con el mismo hilo narrativo que "Cómo funciona".
const FEATURE_GROUP_RANGES = [[0, 1], [1, 4], [4, 8]];
const FEATURE_GROUP_ACCENTS = [
  { badge: "bg-brand-600 text-white", icon: "bg-brand-50 text-brand-600" },
  { badge: "bg-amber-500 text-white", icon: "bg-amber-50 text-amber-600" },
  { badge: "bg-emerald-600 text-white", icon: "bg-emerald-50 text-emerald-600" },
];
const PAIN_ICONS = [Repeat, Scissors, Clock, BatteryLow];
const STEP_ICONS = [TrendingUp, CalendarDays, Sparkles, Youtube];
// Precio mensual numérico (€); el anual se calcula como mensual x 10 (regala 2 meses).
// "gratis" salió de la tabla (ago-2026): sigue existiendo como estado de una cuenta que aún no ha
// pagado, pero no se anuncia. Generar guion y narrar exigen plan de pago, así que no podía
// producir NADA y aun así prometía "prueba el flujo completo con un vídeo corto".
const PLAN_META = [
  { id: "starter", priceMonthly: 20, highlight: false },
  { id: "creator", priceMonthly: 80, highlight: true },
  { id: "pro", priceMonthly: 150, highlight: false },
  { id: "studio", priceMonthly: 500, highlight: false },
];


// Vídeos reales publicados con YTMethod, servidos desde NUESTRO almacenamiento — no desde
// YouTube. Es el mismo `<video>` nativo que ya usa la pestaña de Publicar (TabDescarga.jsx),
// apuntando a /api/video/, que ya sirve con Range (206 + Content-Range) y es ruta pública.
// Así no se carga JavaScript ajeno ni se dejan cookies de terceros en la portada.
const EXAMPLE_MEDIA = [
  {
    v: "videobuilder/renders/6a46212380037e3f7e3e5a78/47a55dec-17b6-4a89-b1a7-d6a3b2441ca3/999716f3-2223-4811-8826-5b3db2ea68ba.mp4",
    p: "/ejemplos/kamchatka.jpg",
  },
  {
    v: "videobuilder/renders/6a46212380037e3f7e3e5a78/974b6de0-5626-4ed3-ba7c-39da2be77a8e/61963336-21c1-4efb-855c-49e8d2750788.mp4",
    p: "/ejemplos/oceano.jpg",
  },
  {
    v: "videobuilder/renders/6a46212380037e3f7e3e5a78/3c965a67-450f-4f2f-8630-24c1ba809566/915737b1-88f5-46a5-adae-b20b16026279.mp4",
    p: "/ejemplos/stanley.jpg",
  },
];
// Vídeo del hero: Socotra. Va aparte de EXAMPLE_MEDIA a propósito — el de portada y los
// ejemplos se eligen por criterios distintos y no deben quedar acoplados.
const HERO_MEDIA = {
  v: "videobuilder/renders/6a46212380037e3f7e3e5a78/0d385cfd-6c5c-450f-8bf1-802ffbe9eb95/ed7dd08d-cc27-4ffc-8a9a-3788eb02031d.mp4",
  p: "/ejemplos/socotra.jpg",
};
const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/video/`;

function VideoEjemplo({ v, i }) {
  return (
    <Reveal i={i} className="bg-white border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-lg transition-shadow">
      {/* preload="none": estos renders son los originales completos (de 400 MB a 1,2 GB). Sin
          esto el navegador se traería metadatos de los tres solo por abrir la portada. Con la
          petición Range que ya implementa /api/video/, al pulsar solo se descarga lo que se ve. */}
      <video
        src={MEDIA_BASE + v.src} poster={v.poster}
        controls preload="none" playsInline
        className="w-full aspect-video bg-black"
        data-testid={`example-video-${i}`}
      />
      <div className="p-5">
        <h3 className="text-base font-bold text-slate-900 leading-snug">{v.title}</h3>
        <p className="text-sm text-slate-600 mt-1">{v.meta}</p>
      </div>
    </Reveal>
  );
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState(0);
  const [seats, setSeats] = useState(null);
  // Por debajo de lg (1024px) el nav de texto no cabe en una fila con logo+login+CTA+idioma —
  // en vez de apretujarlo, se oculta y se ofrece en este menú aparte (mismo breakpoint que
  // usa <nav> más abajo). Antes de esto no había NINGÚN equivalente por debajo de lg: los 5
  // enlaces (Ejemplos, Funcionalidades...) eran inalcanzables en móvil/tablet salvo a scroll.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "monthly" | "annual"

  // Enlace de referido (?ref=CODIGO): se guarda al llegar y se consume al REGISTRARSE, no aquí
  // — entre el clic y el alta el usuario suele darse una vuelta por la landing, y en móvil puede
  // pasar por Google y volver. Sobrevive a esa vuelta porque vive en localStorage.
  //
  // Se limpia el parámetro de la URL para que no se quede colgado si el visitante comparte la
  // dirección desde la barra del navegador: si no, reenviaría a otros el código de quien se lo
  // pasó a él, y la atribución acabaría en la persona equivocada.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (!ref) return;
    localStorage.setItem("vb_ref", ref);
    const u = new URL(window.location.href);
    u.searchParams.delete("ref");
    window.history.replaceState({}, "", u.pathname + u.search + u.hash);
  }, []);

  // Enlace directo con ancla (p.ej. /#precios desde un email o un anuncio): el auto-scroll
  // nativo del navegador al #hash no funciona en una SPA — al llegar la URL, el HTML inicial
  // está casi vacío (React aún no ha montado esta página), así que el navegador busca el
  // elemento, no lo encuentra y se rinde; para cuando React termina de pintarlo ya no vuelve a
  // intentarlo. Se resuelve a mano tras el primer render, descontando la altura del header
  // sticky para que el título de la sección no quede tapado debajo de él.
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    // setTimeout, no requestAnimationFrame: hay vídeos/imágenes en secciones anteriores
    // (hero, ejemplos) que todavía pueden estar cargando y desplazando el layout hacia abajo un
    // frame después del montaje — un pequeño margen reduce el riesgo de calcular la posición
    // justo antes de ese desplazamiento.
    const tid = setTimeout(() => {
      const el = document.getElementById(hash);
      if (!el) return;
      const headerH = document.querySelector("header")?.offsetHeight || 0;
      const top = el.getBoundingClientRect().top + window.scrollY - headerH + 80;
      window.scrollTo({ top, behavior: "smooth" });
    }, 150);
    return () => clearTimeout(tid);
  }, []);
  const setPlan = (k, period = billingPeriod) => {
    if (k) { localStorage.setItem("vb_pending_plan", k); localStorage.setItem("vb_pending_period", period); }
    else { localStorage.removeItem("vb_pending_plan"); localStorage.removeItem("vb_pending_period"); }
  };

  // Los CTA de la landing van directos a Google en vez de pasar por /login (ago-2026): un clic
  // menos hasta la app. /login se queda solo como destino de las redirecciones AUTOMÁTICAS sin
  // clic (sesión caducada, URL protegida sin sesión — ver Protected en App.js) porque el popup
  // de Google necesita un gesto real del usuario para no chocar con el bloqueo de popups del
  // navegador. Tras el login con éxito no hace falta navegar a mano: AppRoutes ya sustituye
  // <Landing/> por <Navigate to="/app"/> en cuanto el user del contexto deja de ser falsy.
  const { loginWithGoogle, apiErr } = useAuth();
  const navigate = useNavigate();
  const [loginBusy, setLoginBusy] = useState(false);
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setLoginBusy(true);
      try { await loginWithGoogle(code); }
      catch (e2) {
        const d = e2?.response?.data?.detail;
        if (d?.error === "seats_full") {
          // Plazas agotadas: en vez de duplicar el formulario de lista de espera en cada uno de
          // los 5 sitios que disparan el login, se manda a /login — ahí ya vive ese formulario
          // completo, y un segundo clic en Google (Google recuerda la cuenta) lo enseña.
          toast.info(t("landing.seatsFullRedirect"));
          navigate("/login");
        } else {
          toast.error(apiErr(e2));
        }
      } finally { setLoginBusy(false); }
    },
    onError: () => toast.error(t("auth.googleError")),
  });
  const startLogin = (planId) => { setPlan(planId); googleLogin(); };

  useEffect(() => {
    api.get("/plan-seats").then((r) => setSeats(r.data?.plans || {})).catch(() => setSeats({}));
  }, []);

  const fmtWeekEnd = (iso) => fmtWeekEndShared(iso, i18n.language);

  const pains = t("landing.pains", { returnObjects: true });
  const steps = t("landing.steps", { returnObjects: true });
  const features = t("landing.features", { returnObjects: true });
  const featureGroupTitles = t("landing.featureGroupTitles", { returnObjects: true });
  const benefits = t("landing.benefits", { returnObjects: true });
  const faqs = t("landing.faqs", { returnObjects: true });
  const examples = t("landing.examples", { returnObjects: true })
    .map((e, i) => ({ ...e, src: (EXAMPLE_MEDIA[i] || {}).v, poster: (EXAMPLE_MEDIA[i] || {}).p }))
    .filter((e) => e.src);

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Organization",
      name: "YTMethod", url: SITE_URL, logo: `${SITE_URL}/ytmethod_transparent.png`,
    },
    {
      "@context": "https://schema.org", "@type": "SoftwareApplication",
      name: "YTMethod", applicationCategory: "MultimediaApplication",
      operatingSystem: "Web", url: SITE_URL,
      // El <1>...</1> de la traducción es para <Trans> (negrita del hero) — aquí solo texto
      // plano, así que se quita antes de usarlo como description SEO/JSON-LD.
      description: t("landing.heroSubtitle").replace(/<\/?1>/g, ""),
      offers: PLAN_META.map((meta) => ({
        "@type": "Offer", name: t(`landing.plans.${meta.id}.name`),
        price: String(meta.priceMonthly), priceCurrency: "EUR",
      })),
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: (faqs || []).map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-slate-900" data-testid="landing-page">
      <Seo title={t("landing.seoTitle")} description={t("landing.seoDescription")} path="/" jsonLd={jsonLd} />
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b-2 border-slate-200">
        {/* h-16 (antes h-20) + tipografía/paddings un escalón por debajo en todos los elementos:
            pedido explícito de bajar el TAMAÑO del header (no el color) tomando como referencia
            un SaaS de IA con header bajo y compacto — el nuestro se sentía "gordo" en comparación
            (80px de alto, botones con py-3, texto text-lg en todos lados). */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-16 w-auto" data-testid="landing-logo" />
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 text-sm xl:text-base font-semibold text-slate-600">
            <a href="#como-funciona" className="hover:text-slate-900 transition-colors whitespace-nowrap">{t("landing.nav.how")}</a>
            <a href="#ejemplos" className="hover:text-slate-900 transition-colors">{t("landing.nav.ejemplos")}</a>
            <a href="#funcionalidades" className="hover:text-slate-900 transition-colors">{t("landing.nav.features")}</a>
            <a href="#precios" className="hover:text-slate-900 transition-colors">{t("landing.nav.pricing")}</a>
            <a href="#preguntas" className="hover:text-slate-900 transition-colors">{t("landing.nav.faq")}</a>
          </nav>
          <div className="flex items-center gap-2.5">
            {/* Antes había un botón "Iniciar sesión" aparte junto a este — se quitó (ago-2026):
                Google gestiona alta y entrada con el mismo flujo, así que un botón de login
                separado era redundante. "Empezar ahora" hace ahora lo que hacía "Iniciar
                sesión" (Google directo, sin plan preseleccionado) en vez de bajar a #precios. */}
            <button type="button" onClick={() => startLogin(null)} disabled={loginBusy} className="inline-flex items-center gap-1.5 bg-brand-600 text-white hover:bg-brand-700 transition-colors text-sm font-bold px-4 py-2 rounded-full disabled:opacity-60" data-testid="landing-header-cta">{t("landing.startFree")}</button>
            <button type="button" onClick={() => setMobileMenuOpen((v) => !v)}
              aria-expanded={mobileMenuOpen} aria-controls="landing-mobile-menu" aria-label={t("landing.nav.toggleMenu")}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
              data-testid="landing-mobile-menu-button">
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div id="landing-mobile-menu" className="lg:hidden border-t-2 border-slate-200 bg-white" data-testid="landing-mobile-menu">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col text-base font-semibold text-slate-700">
              <a href="#como-funciona" onClick={closeMobileMenu} className="py-2.5 hover:text-slate-900">{t("landing.nav.how")}</a>
              <a href="#ejemplos" onClick={closeMobileMenu} className="py-2.5 hover:text-slate-900">{t("landing.nav.ejemplos")}</a>
              <a href="#funcionalidades" onClick={closeMobileMenu} className="py-2.5 hover:text-slate-900">{t("landing.nav.features")}</a>
              <a href="#precios" onClick={closeMobileMenu} className="py-2.5 hover:text-slate-900">{t("landing.nav.pricing")}</a>
              <a href="#preguntas" onClick={closeMobileMenu} className="py-2.5 hover:text-slate-900">{t("landing.nav.faq")}</a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <Section className="!pt-16 md:!pt-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">
              {t("landing.heroTitle")}<br />
              <span className="text-brand-600">{t("landing.heroHighlight")}</span><br />
              {t("landing.heroTitle2")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              <Trans i18nKey="landing.heroSubtitle" components={{ 1: <strong className="font-bold text-slate-900" /> }} />
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button type="button" onClick={() => startLogin(null)} disabled={loginBusy} className={`${btnPrimary} disabled:opacity-60`} data-testid="hero-primary-cta">{t("landing.startFree")} <ArrowRight size={22} /></button>
              <a href="#precios" className={btnSecondary} data-testid="hero-secondary-cta">{t("landing.seePlans")}</a>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-base text-slate-500 font-medium">
              <span className="flex items-center gap-2"><ShieldCheck size={20} className="text-emerald-600 shrink-0" /> {t("landing.noCard")}</span>
              <span className="flex items-center gap-2"><CalendarX size={20} className="text-emerald-600 shrink-0" /> {t("landing.trustCancel")}</span>
              <span className="flex items-center gap-2"><Lock size={20} className="text-emerald-600 shrink-0" /> {t("landing.trustSecure")}</span>
            </div>
          </Reveal>
          <Reveal i={1}>
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-600/10 rounded-[2.5rem] blur-2xl" />
              {/* Vídeo real en vez de la foto de stock que llevaba aquí desde el principio.
                  preload="none" y sin autoplay a propósito: el fichero es el render completo, y
                  arrancarlo solo por cargar la portada descargaría cientos de MB a cada visitante,
                  incluidos los que se van a los tres segundos. Se ve el póster y decide el visitante. */}
              <video
                src={MEDIA_BASE + HERO_MEDIA.v} poster={HERO_MEDIA.p}
                controls preload="none" playsInline
                aria-label={t("landing.heroImgAlt")} data-testid="hero-video"
                className="relative w-full rounded-[2rem] shadow-2xl bg-black aspect-video"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Pain points */}
      <Section className="bg-[#0F172A] text-white">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">{t("landing.painTitle")}</h2></Reveal>
        <Reveal i={1}><p className="text-lg text-slate-300 text-center mt-3 max-w-2xl mx-auto">{t("landing.painSubtitle")}</p></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {pains.map((p, i) => {
            const Icon = PAIN_ICONS[i];
            return (
              <Reveal key={i} i={i} className="bg-white/5 border-2 border-white/10 rounded-3xl p-8">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
                  <Icon size={30} className="text-amber-300" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold mb-2">{p.title}</h3>
                <p className="text-base text-slate-300 leading-relaxed">{p.desc}</p>
              </Reveal>
            );
          })}
        </div>
        <Reveal i={2}><p className="text-center text-lg text-white mt-12 font-semibold">{t("landing.painFooter")}<span className="text-amber-300">{t("landing.painFooterHighlight")}</span></p></Reveal>
      </Section>

      {/* How it works */}
      <Section id="como-funciona" className="bg-white">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">{t("landing.howTitle")}</h2></Reveal>
        <Reveal i={1}><p className="text-lg text-slate-600 text-center mt-3 max-w-2xl mx-auto">{t("landing.howSubtitle")}</p></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <Reveal key={i} i={i} className="text-center bg-[#FAFAF9] border-2 border-slate-200 rounded-3xl p-8 md:p-10">
                <div className="mx-auto w-16 h-16 rounded-full bg-brand-600 text-white flex items-center justify-center text-xl font-extrabold mb-5">{i + 1}</div>
                <Icon size={40} className="mx-auto text-brand-600 mb-4" strokeWidth={2} />
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-base text-slate-600 leading-relaxed">{s.desc}</p>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Vídeos de ejemplo — van justo detrás de "Cómo funciona" a propósito: primero se explica
          la rutina, y acto seguido se enseña lo que sale de ella. La FAQ llevaba meses prometiendo
          "en esta misma página tienes vídeos de ejemplo" sin que existiera ninguno. */}
      <Section id="ejemplos" className="bg-[#FAFAF9]">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">{t("landing.examplesTitle")}</h2></Reveal>
        <Reveal i={1}><p className="text-lg text-slate-600 text-center mt-3 max-w-2xl mx-auto">{t("landing.examplesSubtitle")}</p></Reveal>
        <div className="grid md:grid-cols-3 gap-8 mt-14">
          {examples.map((v, i) => <VideoEjemplo key={v.src} v={v} i={i} />)}
        </div>
      </Section>

      {/* Features */}
      <Section id="funcionalidades" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-0 w-[32rem] h-[32rem] bg-brand-600/5 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-0 w-[28rem] h-[28rem] bg-emerald-400/5 rounded-full blur-3xl" />
        <div className="relative">
          <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">{t("landing.featuresTitle")}</h2></Reveal>
          <Reveal i={1}><p className="text-lg text-slate-600 text-center mt-3 max-w-2xl mx-auto">{t("landing.featuresSubtitle")}</p></Reveal>

          {FEATURE_GROUP_RANGES.map(([start, end], gi) => {
            const accent = FEATURE_GROUP_ACCENTS[gi];
            const items = features.slice(start, end);
            const isHeroGroup = items.length === 1;
            return (
              <div key={gi} className={gi === 0 ? "mt-14" : "mt-16"}>
                <Reveal className="flex items-center gap-3 mb-6">
                  <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-base font-extrabold ${accent.badge}`}>{gi + 1}</span>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">{featureGroupTitles[gi]}</h3>
                </Reveal>

                {isHeroGroup ? (
                  <Reveal className="bg-white border-2 border-slate-200 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 hover:shadow-lg transition-all">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${accent.icon}`}>
                      {(() => { const Icon = FEATURE_ICONS[start]; return <Icon size={32} strokeWidth={2} />; })()}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-2">{items[0].title}</h4>
                      <p className="text-base text-slate-600 leading-relaxed">{items[0].desc}</p>
                    </div>
                  </Reveal>
                ) : (
                  <div className={`grid sm:grid-cols-2 ${end - start > 4 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6 md:gap-7`}>
                    {items.map((f, ii) => {
                      const Icon = FEATURE_ICONS[start + ii];
                      return (
                        <Reveal key={ii} i={ii % 3} className="bg-white border-2 border-slate-200 rounded-3xl p-7 hover:shadow-lg hover:-translate-y-1 transition-all">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${accent.icon}`}>
                            <Icon size={28} strokeWidth={2} />
                          </div>
                          <h4 className="text-lg font-bold mb-2">{f.title}</h4>
                          <p className="text-base text-slate-600 leading-relaxed">{f.desc}</p>
                        </Reveal>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Benefits */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 bg-amber-300/20 rounded-[2.5rem] blur-2xl" />
              <img src={BENEFITS_IMG} alt={t("landing.benefitsImgAlt")} className="relative w-full rounded-[2rem] shadow-2xl object-cover aspect-[4/3]" />
            </div>
          </Reveal>
          <Reveal i={1}>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("landing.benefitsTitle")}</h2>
            <ul className="mt-8 space-y-5">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5"><Check size={20} className="text-emerald-600" strokeWidth={3} /></span>
                  <span className="text-lg text-slate-700 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => startLogin(null)} disabled={loginBusy} className={`${btnPrimary} mt-9 disabled:opacity-60`} data-testid="benefits-cta">{t("landing.benefitsCta")} <ArrowRight size={22} /></button>
          </Reveal>
        </div>
      </Section>

      {/* Pricing */}
      <Section id="precios" className="bg-white">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">{t("landing.pricingTitle")}</h2></Reveal>
        <Reveal i={1}><p className="text-lg text-slate-600 text-center mt-3">{t("landing.pricingSubtitle")}</p></Reveal>
        <Reveal i={2}><p className="text-base text-slate-500 text-center mt-4 max-w-2xl mx-auto">{t("landing.scarcityLine")}</p></Reveal>

        <Reveal i={2} className="flex justify-center mt-8">
          <div className="inline-flex items-center bg-slate-100 rounded-full p-1.5 gap-1" role="group" aria-label={t("landing.pricingTitle")}>
            <button type="button" onClick={() => setBillingPeriod("monthly")}
              className={`px-5 py-2.5 rounded-full text-base font-bold transition-all ${billingPeriod === "monthly" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}>
              {t("landing.billingMonthly")}
            </button>
            <button type="button" onClick={() => setBillingPeriod("annual")}
              className={`px-5 py-2.5 rounded-full text-base font-bold transition-all ${billingPeriod === "annual" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}>
              {t("landing.billingAnnual")}
            </button>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10 items-stretch">
          {PLAN_META.map((meta, i) => {
            const plan = t(`landing.plans.${meta.id}`, { returnObjects: true });
            const isFree = meta.priceMonthly === 0;
            const displayPrice = billingPeriod === "annual" && !isFree ? meta.priceMonthly * 10 : meta.priceMonthly;
            return (
              <Reveal key={meta.id} i={i} className={`relative flex flex-col rounded-3xl p-8 border-2 ${meta.highlight ? "border-brand-600 bg-brand-50/40 shadow-xl lg:scale-105" : "border-slate-200 bg-white"}`}>
                {meta.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-600 text-white text-sm font-bold px-4 py-1 rounded-full whitespace-nowrap">{t("landing.badgePopular")}</span>}
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <div className="mt-3 mb-2">
                  <span className="text-4xl font-extrabold">{displayPrice}€</span>
                  <span className="text-base text-slate-500 font-medium">{isFree ? t("landing.perMonth") : billingPeriod === "annual" ? t("landing.perYear") : t("landing.perMonth")}</span>
                </div>
                {!isFree && billingPeriod === "annual" && <p className="text-sm text-emerald-600 font-semibold mb-4">{t("landing.annualNote")}</p>}
                {isFree && <div className="mb-4" />}
                {plan.resultLine && (
                  <p className="text-base font-bold text-brand-700 mb-1">{plan.resultLine}</p>
                )}
                <ul className="space-y-3 flex-1">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className={`flex items-start gap-3 text-slate-700 ${fi === 0 && plan.resultLine ? "text-sm text-slate-500" : "text-base"}`}>
                      <Check size={20} className="text-emerald-600 mt-1 shrink-0" strokeWidth={3} /> {f}
                    </li>
                  ))}
                </ul>
                <SeatBadge plan={meta.id} info={seats?.[meta.id]} weekEndLabel={fmtWeekEnd(seats?.[meta.id]?.week_ends_at)} />
                <button type="button" onClick={() => startLogin(meta.id === "gratis" ? null : meta.id)} disabled={loginBusy} data-testid={`plan-cta-${meta.id}`} className={`mt-4 inline-flex items-center justify-center gap-2 text-base font-bold px-6 py-4 rounded-full transition-all disabled:opacity-60 ${meta.highlight ? "bg-brand-600 text-white hover:bg-brand-700" : "bg-white text-brand-700 border-2 border-brand-600 hover:bg-brand-50"}`}>{plan.cta}</button>
              </Reveal>
            );
          })}
        </div>
        <Reveal i={3}><p className="text-center text-base text-slate-600 font-medium mt-10">{t("landing.plansIncludeShared")}</p></Reveal>
        <Reveal i={3}><p className="text-center text-base text-slate-500 mt-4 flex items-center justify-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> {t("landing.securePay")}</p></Reveal>
      </Section>

      {/* FAQ */}
      <Section id="preguntas">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-12">{t("landing.faqTitle")}</h2></Reveal>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((f, i) => (
            <Reveal key={i} i={i}>
              <div className="border-2 border-slate-200 rounded-2xl overflow-hidden bg-white">
                <button data-testid={`faq-toggle-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 min-h-[64px]">
                  <span className="text-lg font-bold text-slate-900">{f.q}</span>
                  <ChevronDown size={26} className={`shrink-0 text-brand-600 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-5 text-base text-slate-600 leading-relaxed">{f.a}</div>}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="!py-20">
        <Reveal className="bg-brand-600 rounded-[2.5rem] px-8 py-14 md:py-20 text-center">
          <Heart size={44} className="mx-auto text-white/90 mb-5" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("landing.finalTitle")}</h2>
          <p className="text-lg text-brand-100 mt-4 max-w-2xl mx-auto">{t("landing.finalSubtitle")}</p>
          <button type="button" onClick={() => startLogin(null)} disabled={loginBusy} className="inline-flex items-center gap-2 bg-white text-brand-700 hover:scale-[1.03] transition-transform text-lg font-bold px-10 py-5 rounded-full shadow-xl mt-8 disabled:opacity-60" data-testid="final-cta">{t("landing.startFree")} <ArrowRight size={24} /></button>
        </Reveal>
      </Section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white px-6 md:px-12 py-16">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-start">
          <div>
            <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-10 w-auto mb-4" style={{ filter: "brightness(0) invert(1)" }} />
            <p className="text-base text-slate-300 max-w-md leading-relaxed">{t("landing.footerDesc")}</p>
          </div>
          <div className="md:text-right">
            <div className="flex flex-wrap md:justify-end gap-x-8 gap-y-3 text-base text-slate-300">
              <a href="#precios" className="hover:text-white transition-colors">{t("landing.nav.pricing")}</a>
              <a href="#preguntas" className="hover:text-white transition-colors">{t("landing.nav.faq")}</a>
              <Link to="/docs" className="hover:text-white transition-colors">Documentación</Link>
              <Link to="/privacidad" className="hover:text-white transition-colors">{t("landing.footerPrivacy")}</Link>
              <Link to="/terminos" className="hover:text-white transition-colors">{t("landing.footerTerms")}</Link>
              <button type="button" onClick={() => startLogin(null)} disabled={loginBusy} className="hover:text-white transition-colors disabled:opacity-60">{t("landing.login")}</button>
            </div>
            <div className="flex md:justify-end mt-5">
              <LanguageSwitcher />
            </div>
            <p className="text-base text-slate-400 mt-8">{t("landing.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
